// frontend/src/components/voice/CallHistoryTable.js

import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/tooltip';
import {
    Play,
    FileText,
    Search,
    PhoneIncoming,
    PhoneOutgoing,
    RefreshCw
} from 'lucide-react';
import { useCallHistory } from '../../hooks/useVoiceAPI';
import { formatDuration, formatPhoneNumber, formatDate } from '../../lib/utils';

const CallHistoryTable = ({ profileId }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [outcomeFilter, setOutcomeFilter] = useState('all');
    const [selectedCall, setSelectedCall] = useState(null);
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

    const filters = React.useMemo(() => ({
        search: searchQuery,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
    }), [searchQuery, statusFilter, outcomeFilter]);

    const { calls, loading, refetch } = useCallHistory(profileId || 'default', filters);

    // [POLISH] Sort calls by date (newest first)
    const sortedCalls = React.useMemo(() => {
        return [...(calls || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [calls]);

    const formatReason = (reason) => {
        if (!reason || reason === '-') return '—';
        const map = {
            'customer-ended-call': 'Customer hung up',
            'assistant-forwarded-call': 'Forwarded',
            'assistant-ended-call': 'Assistant ended',
            'voicemail': 'Voicemail',
            'silence-timed-out': 'Silence timeout'
        };
        return map[reason] || reason.replace(/-/g, ' ');
    };

    const getStatusBadge = (status) => {
        if (!status) return <span className="text-gray-400 text-xs">—</span>;
        const s = status.toLowerCase();
        const variants = {
            completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            missed: 'bg-rose-50 text-rose-700 border-rose-200',
            voicemail: 'bg-blue-50 text-blue-700 border-blue-200',
            failed: 'bg-gray-50 text-gray-700 border-gray-200',
            in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className={`text-xs uppercase font-semibold ${variants[s] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                            {status}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {s === 'completed' ? 'Call completed successfully' :
                            s === 'missed' ? 'Call was missed' :
                                s === 'voicemail' ? 'Caller left a voicemail' :
                                    s === 'in_progress' ? 'Call is active' :
                                        'Call failed to connect'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const getOutcomeBadge = (outcome) => {
        if (!outcome || outcome === 'None' || outcome === '-') {
            return <span className="text-gray-400 text-xs">—</span>;
        }
        const o = outcome.toLowerCase();
        const variants = {
            booked: 'bg-purple-50 text-purple-700 border-purple-200',
            transferred: 'bg-amber-50 text-amber-800 border-amber-200',
            hangup: 'bg-gray-100 text-gray-700 border-gray-200',
            voicemail: 'bg-blue-50 text-blue-700 border-blue-200',
            completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className={`text-xs capitalize font-medium ${variants[o] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            {o}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {o === 'booked' ? 'Appointment booked successfully' :
                            o === 'transferred' ? 'Call transferred to attorney' :
                                o === 'voicemail' ? 'Caller reached voicemail' :
                                    o === 'completed' ? 'Call completed successfully' :
                                        'Caller hung up'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const getSentimentBadge = (sentiment) => {
        if (!sentiment || sentiment === 'None' || sentiment === '-') {
            return <span className="text-gray-400 text-xs">—</span>;
        }
        const s = sentiment.toLowerCase();
        const variants = {
            positive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            neutral: 'bg-slate-100 text-slate-700 border-slate-200',
            negative: 'bg-amber-50 text-amber-800 border-amber-200',
            angry: 'bg-rose-50 text-rose-700 border-rose-200',
        };
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className={`text-xs capitalize font-medium ${variants[s] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                            {s}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        {s === 'positive' ? 'Caller was satisfied' :
                            s === 'negative' ? 'Caller expressed dissatisfaction' :
                                s === 'angry' ? 'Caller was angry/upset' :
                                    'Caller was neutral'}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const handleViewTranscript = (call) => {
        setSelectedCall(call);
        setIsTranscriptOpen(true);
    };

    const handlePlayRecording = (call) => {
        setSelectedCall(call);
        setIsTranscriptOpen(true);
    };

    if (loading) {
        return (
            <div className="text-center py-8 text-gray-500">
                Loading call history...
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} color="#9ca3af" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                        <Input
                            placeholder="Search by phone number..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: '2.5rem' }}
                        />
                    </div>
                </div>
                <div style={{ width: '180px' }}>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="missed">Missed</SelectItem>
                            <SelectItem value="voicemail">Voicemail</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div style={{ width: '180px' }}>
                    <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Outcome" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Outcomes</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="transferred">Transferred</SelectItem>
                            <SelectItem value="hangup">Hangup</SelectItem>
                            <SelectItem value="voicemail">Voicemail</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {/* [POLISH] Refresh Button */}
                <Button variant="outline" size="sm" onClick={refetch}>
                    <RefreshCw size={16} className="mr-2" /> Refresh
                </Button>
            </div>

            {/* Table Container with full responsive horizontal scroll */}
            <div
                style={{
                    border: '1px solid #E5E1D6',
                    borderRadius: '0.75rem',
                    overflowX: 'auto',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)'
                }}
            >
                <Table style={{ minWidth: '1150px', width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHeader>
                        <TableRow style={{ backgroundColor: '#F8F7F2', borderBottom: '1px solid #E5E1D6' }}>
                            <TableHead style={{ minWidth: '130px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Date & Time</TableHead>
                            <TableHead style={{ minWidth: '145px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Caller</TableHead>
                            <TableHead style={{ width: '75px', textAlign: 'center', whiteSpace: 'nowrap', padding: '0.75rem 0.5rem', color: '#687077', fontWeight: 600 }}>Direction</TableHead>
                            <TableHead style={{ minWidth: '85px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Duration</TableHead>
                            <TableHead style={{ minWidth: '75px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Cost</TableHead>
                            <TableHead style={{ minWidth: '160px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Reason</TableHead>
                            <TableHead style={{ minWidth: '110px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Intent</TableHead>
                            <TableHead style={{ minWidth: '100px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Sentiment</TableHead>
                            <TableHead style={{ minWidth: '115px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Status</TableHead>
                            <TableHead style={{ minWidth: '115px', whiteSpace: 'nowrap', padding: '0.75rem 1rem', color: '#687077', fontWeight: 600 }}>Outcome</TableHead>
                            <TableHead style={{
                                minWidth: '95px',
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                padding: '0.75rem 1rem',
                                color: '#687077',
                                fontWeight: 600,
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#F8F7F2',
                                borderLeft: '1px solid #E5E1D6',
                                boxShadow: '-2px 0 4px rgba(32, 35, 31, 0.04)'
                            }}>
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedCalls && sortedCalls.length > 0 ? (
                            sortedCalls.map((call) => (
                                <TableRow key={call.id} style={{ borderBottom: '1px solid #F1EEE4', transition: 'background-color 0.15s' }}>
                                    <TableCell style={{ fontWeight: '500', whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#20231F' }}>
                                        {formatDate(call.created_at)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.8125rem', color: '#20231F' }}>
                                        {formatPhoneNumber(call.caller_phone || call.caller_number)}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}>
                                        {call.direction === 'inbound' ? (
                                            <span title="Inbound call" className="inline-flex items-center justify-center p-1 rounded-full bg-emerald-50 text-emerald-600">
                                                <PhoneIncoming size={14} />
                                            </span>
                                        ) : (
                                            <span title="Outbound call" className="inline-flex items-center justify-center p-1 rounded-full bg-blue-50 text-blue-600">
                                                <PhoneOutgoing size={14} />
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#20231F', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatDuration(call.duration_seconds)}
                                    </TableCell>
                                    <TableCell style={{
                                        whiteSpace: 'nowrap',
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.8125rem',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: (call.cost || 0) > 2.00 ? '#ef4444' : '#20231F',
                                        fontWeight: (call.cost || 0) > 2.00 ? '600' : 'normal'
                                    }}>
                                        ${(call.cost || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        <Badge variant="outline" className="text-[11px] font-normal text-[#687077] border-[#E5E1D6] bg-[#F8F7F2]">
                                            {formatReason(call.ended_reason)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#687077' }}>
                                        {call.lead_intent && call.lead_intent !== 'N/A' ? (
                                            <Badge variant="outline" className="text-xs">{call.lead_intent}</Badge>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getSentimentBadge(call.sentiment)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getStatusBadge(call.status)}
                                    </TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem' }}>
                                        {getOutcomeBadge(call.outcome)}
                                    </TableCell>
                                    <TableCell style={{
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        padding: '0.75rem 1rem',
                                        position: 'sticky',
                                        right: 0,
                                        backgroundColor: '#ffffff',
                                        borderLeft: '1px solid #E5E1D6',
                                        boxShadow: '-2px 0 4px rgba(32, 35, 31, 0.04)'
                                    }}>
                                        <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center' }}>
                                            {(call.recording_url || call.transcript_json) ? (
                                                <>
                                                    {call.recording_url && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handlePlayRecording(call)}
                                                            style={{ padding: '0.35rem 0.5rem', height: '2rem', color: '#C8A653' }}
                                                            title="Слушать аудиозапись"
                                                        >
                                                            <Play size={15} />
                                                        </Button>
                                                    )}
                                                    {call.transcript_json && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleViewTranscript(call)}
                                                            style={{ padding: '0.35rem 0.5rem', height: '2rem', color: '#20231F' }}
                                                            title="Посмотреть расшифровку звонка"
                                                        >
                                                            <FileText size={15} />
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} className="text-center py-12 text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <PhoneIncoming size={32} className="text-gray-400" />
                                        <p className="font-semibold text-gray-700">No calls found</p>
                                        <p className="text-sm text-gray-400">Incoming and outgoing calls will appear here</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Transcript & Recording Dialog */}
            <Dialog open={isTranscriptOpen} onOpenChange={setIsTranscriptOpen}>
                <DialogContent style={{ maxWidth: '48rem', maxHeight: '85vh', overflowY: 'auto' }}>
                    <DialogHeader>
                        <DialogTitle>Call Details & Transcript</DialogTitle>
                        <DialogDescription>
                            {selectedCall && formatPhoneNumber(selectedCall.caller_phone || selectedCall.caller_number)} •{' '}
                            {selectedCall && formatDate(selectedCall.created_at)}
                            {selectedCall?.duration_seconds ? ` • ${formatDuration(selectedCall.duration_seconds)}` : ''}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Audio Player if recording_url exists */}
                    {selectedCall?.recording_url && (
                        <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', backgroundColor: '#20231F', borderRadius: '0.5rem', color: '#ffffff' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#C8A653', display: 'block', marginBottom: '0.35rem' }}>
                                Audio Recording
                            </span>
                            <audio controls src={selectedCall.recording_url} style={{ width: '100%', height: '36px' }} autoPlay />
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                        {selectedCall?.transcript_json?.messages && selectedCall.transcript_json.messages.length > 0 ? (
                            selectedCall.transcript_json.messages.map((message, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end' }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '80%',
                                            borderRadius: '0.5rem',
                                            padding: '0.75rem',
                                            backgroundColor: message.role === 'assistant' ? '#eff6ff' : '#f0fdf4',
                                            color: message.role === 'assistant' ? '#1e3a8a' : '#14532d'
                                        }}
                                    >
                                        <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                                            {message.role === 'assistant' ? 'Sofia (AI Assistant)' : 'Caller'}
                                        </div>
                                        <p style={{ fontSize: '0.875rem', margin: 0 }}>{message.content}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ color: '#687077', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>
                                Transcript not available for this call.
                            </p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default CallHistoryTable;
